import React from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import useBundleTranslation from 'i18n';
import { FormComponentBuilder } from 'components/common/form/layout';
import {
    FormComponentValue,
    FormControlProps,
    FormElementControlPropsType,
} from 'components/common/form/layout/control';
import SourceTypeIcon from 'components/common/source-type-icon/SourceTypeIcon';
import { IKgModelCard } from 'app/extreport/concierge/answering/kgModelCardTypes';
import { deriveRowStatus } from './rowStatus';
import { useConciergeKgCards } from './useConciergeKgCards';

interface AnswerSourceToggleProps extends FormControlProps {
    data: Array<FormComponentValue>;
    biTool?: 'powerbi' | 'tableau' | 'other';
    models: IKgModelCard[];
    entityId: number;
    actionUrl: string;
    legacyOwnActive?: boolean;
}

export default function AnswerSourceToggle({ controlProps }: FormElementControlPropsType<AnswerSourceToggleProps>) {
    const { t } = useBundleTranslation(['components/common/concierge-answering']);
    const { cards } = useConciergeKgCards({
        entityId: controlProps.entityId,
        actionUrl: controlProps.actionUrl,
        initial: controlProps.models ?? [],
    });
    const value = controlProps.value ? String(controlProps.value) : '';
    // A published own model always answers first, so another resource cannot be chosen over it. A pick
    // stored before the model was published stays as the fallback until "own" is saved, which clears it on the backend.
    const ownPublished =
        cards.some((card) => deriveRowStatus(card) === 'published') ||
        (cards.length === 0 && !!controlProps.legacyOwnActive);
    const otherLocked = ownPublished && 'own' === value;
    const platform =
        'powerbi' === controlProps.biTool || 'tableau' === controlProps.biTool ? controlProps.biTool : null;

    return (
        <ToggleButtonGroup
            color="primary"
            exclusive
            value={value}
            disabled={controlProps.readOnly}
            onChange={(_e, next) => {
                if (null === next) return;
                controlProps.onChange(next);
            }}
            data-test={controlProps.uid}
        >
            {controlProps.data
                .filter((opt) => !opt.hidden)
                .map((opt) => {
                    const locked = otherLocked && 'other' === opt.value;
                    const button = (
                        <ToggleButton
                            key={String(opt.value)}
                            value={String(opt.value)}
                            disabled={locked || undefined}
                            data-test={`${controlProps.uid}_${opt.value}`}
                        >
                            {'own' === opt.value && platform && (
                                <Box sx={{ mr: 1, display: 'inline-flex' }}>
                                    <SourceTypeIcon sourceType={platform} />
                                </Box>
                            )}
                            {opt.label}
                        </ToggleButton>
                    );
                    return locked ? (
                        <Tooltip key={String(opt.value)} title={t('tooltip_other_resource_disabled')}>
                            <span data-test={`${controlProps.uid}_${opt.value}_locked`}>{button}</span>
                        </Tooltip>
                    ) : (
                        button
                    );
                })}
        </ToggleButtonGroup>
    );
}

export class AnswerSourceToggleBuilder extends FormComponentBuilder {
    prepareProps(): AnswerSourceToggleProps {
        return {
            ...this.controlProps,
            data: this.componentValues,
            biTool: this.elementProps.componentProps?.biTool,
            models: this.elementProps.componentProps?.models ?? [],
            entityId: this.elementProps.componentProps?.entityId,
            actionUrl: this.elementProps.componentProps?.actionUrl,
            legacyOwnActive: this.elementProps.componentProps?.legacyOwnActive,
        };
    }
}
